// app/src/features/timeline/useBranchLinkFocus.js
import { useEffect, useRef, useState } from "react";

/** How long the linked branch glows unless the viewer interacts first. */
export const BRANCH_LINK_GLOW_MS = 4000;

/** Where the fork date lands when the view has to pan to it, as a fraction of the visible lane. */
const FORK_PAN_FRACTION = 0.3;

/** Inside this margin (a fraction of the visible lane at each edge) a fork counts as off-screen. */
const FORK_EDGE_MARGIN = 0.08;

/** At most this many frames are spent waiting for a running zoom or pan animation to finish. */
const MAX_WAIT_FRAMES = 120;

/** The inputs that end the glow. Listened for in the capture phase: the hotkeys stop their keys. */
const DISMISS_EVENTS = ["pointerdown", "wheel", "keydown"];

/**
 * The forecast view's side of a `?forecast=` link to a branch (SOO-1008). The deep link opens
 * the branch's root and records the request in the run store (`branchLinkFocus`, which also
 * expands the branch); once that branch is laid out on the active forecast, and any zoom or pan
 * animation the opening started has finished, this scrolls its lane into view, pans the time
 * axis to its fork date when that is outside the visible lane, and returns the branch's id as a
 * brief glow, which the caller feeds to the existing hover highlight.
 *
 * Each request (`focus.seq`) is acted on once. The glow ends after BRANCH_LINK_GLOW_MS or on the
 * viewer's first deliberate input (a click, a wheel, a key), whichever comes first; a pointer
 * move does not end it, since the pointer often moves as the page settles.
 *
 * @param {object} args
 * @param {{runId: string, seq: number} | null | undefined} args.focus  the store's branchLinkFocus
 * @param {Array<{runId: string, forkDate?: string | null}>} args.branches  the active forecast's
 *   visible branches, in lane order
 * @param {number[] | null | undefined} args.branchYs  each branch's lane y, indexed like `branches`
 * @param {{current: HTMLElement | null}} args.scrollRef  the timeline's vertical scroll box
 * @param {(d: Date) => number} args.dayToScreen  a date's x on screen
 * @param {number} args.laneLeft  where the lane becomes visible (an open side panel covers the
 *   column to its left)
 * @param {number} args.laneRight  the lane's right edge
 * @param {(dx: number) => void} args.panBy  shifts the time axis so a point moves left by `dx`
 * @param {() => boolean} args.isAnimating  true while a zoom or pan animation is running
 * @param {boolean} args.ready  the timeline is mounted (false while a gate, such as the terms of
 *   service, holds the app back); nothing happens until it is
 * @param {number} args.forkCapMs  the latest moment a fork is drawn at: the lane clamps a later
 *   fork onto the root's resolution line, so this does too
 * @returns {string | null} the branch to glow now, or null
 */
export function useBranchLinkFocus({
  focus,
  branches,
  branchYs,
  scrollRef,
  dayToScreen,
  laneLeft,
  laneRight,
  panBy,
  isAnimating,
  ready,
  forkCapMs,
}) {
  const [dismissedSeq, setDismissedSeq] = useState(0);
  const handledSeqRef = useRef(0);
  const index = focus ? branches.findIndex((b) => b.runId === focus.runId) : -1;
  const y = index >= 0 ? branchYs?.[index] : undefined;
  const laidOut = Boolean(ready) && index >= 0 && Number.isFinite(y);
  const glowId = laidOut && focus.seq > dismissedSeq ? focus.runId : null;
  const seq = focus?.seq ?? 0;
  const forkDate = index >= 0 ? (branches[index].forkDate ?? null) : null;

  // Bring the lane into view once per request. The request is marked handled only when the
  // frame runs, so a layout change that cancels a queued frame leaves it for the next one.
  useEffect(() => {
    if (!laidOut || handledSeqRef.current >= seq) return;
    let raf = 0;
    let waited = 0;
    const step = () => {
      // Opening the root starts a framing animation whose frames would overwrite a pan.
      if (isAnimating() && waited < MAX_WAIT_FRAMES) {
        waited += 1;
        raf = requestAnimationFrame(step);
        return;
      }
      const el = scrollRef.current;
      // Not mounted after all: leave the request for the effect run that `ready` triggers.
      if (!el) return;
      handledSeqRef.current = seq;
      el.scrollTop = Math.max(0, y - el.clientHeight / 2);
      const forkMs = Math.min(forkDate ? Date.parse(forkDate) : NaN, forkCapMs ?? Infinity);
      const width = laneRight - laneLeft;
      if (!Number.isFinite(forkMs) || !(width > 0)) return;
      const x = dayToScreen(new Date(forkMs));
      const margin = width * FORK_EDGE_MARGIN;
      if (x < laneLeft + margin || x > laneRight - margin)
        panBy(x - (laneLeft + width * FORK_PAN_FRACTION));
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [
    laidOut,
    seq,
    y,
    forkDate,
    forkCapMs,
    scrollRef,
    dayToScreen,
    laneLeft,
    laneRight,
    panBy,
    isAnimating,
  ]);

  // End the glow after its time, or on the viewer's first deliberate input.
  useEffect(() => {
    if (!glowId) return;
    const dismiss = () => setDismissedSeq(seq);
    const timer = setTimeout(dismiss, BRANCH_LINK_GLOW_MS);
    for (const e of DISMISS_EVENTS)
      window.addEventListener(e, dismiss, { capture: true, once: true });
    return () => {
      clearTimeout(timer);
      for (const e of DISMISS_EVENTS) window.removeEventListener(e, dismiss, { capture: true });
    };
  }, [glowId, seq]);

  return glowId;
}
